import { TooltipFormatterContextObject } from 'highcharts'
import { SeriesOptionsType } from 'highcharts'
import { DateInterval } from '~common'
import { getCommonChartOptions, getCommonExportChartOptions } from '../../common-charts-options'
import { generateExportChartSourceText, generateExportChartFileName, generateExportChartTitleText } from '../../helpers'
import { ExportChartConfig } from '../../types'

export const useProductsMentionsColumnChartOptions = ({
  data,
  categories,
  exportChartConfig,
  dateInterval,
  toolTipFormatter,
}: {
  data: SeriesOptionsType[]
  categories: number[] | string[]
  exportChartConfig?: ExportChartConfig
  dateInterval: DateInterval
  toolTipFormatter: (context: TooltipFormatterContextObject) => string
}): [Highcharts.Options] => {
  const commonExportOptions = getCommonExportChartOptions(data, dateInterval.breakDownBy)
  const commonChartOptions = getCommonChartOptions(categories)
  const options: Highcharts.Options = {
    ...commonChartOptions,
    exporting: {
      ...(exportChartConfig
        ? {
            ...commonExportOptions,
            tableCaption: generateExportChartTitleText({ exportChartConfig, isTotal: false }),
            chartOptions: {
              ...commonExportOptions.chartOptions,
              plotOptions: {
                series: {
                  dataLabels: {
                    ...commonExportOptions.chartOptions?.plotOptions?.series?.dataLabels,
                    formatter: function (): string {
                      return `${this.y}%`
                    },
                  },
                },
              },

              title: {
                ...commonExportOptions.chartOptions?.title,
                text: generateExportChartTitleText({ exportChartConfig, isTotal: false }),
              },
              subtitle: {
                ...commonExportOptions.chartOptions?.subtitle,
                text: generateExportChartSourceText({
                  exportChartConfig,
                  series: data.filter((el) => el.showInLegend),
                  isTotal: false,
                }),
              },
              chart: {
                ...commonExportOptions.chartOptions?.chart,
                marginLeft: 70,
                marginRight: 50,
                plotBackgroundImage: 'https://app.pharmaspectra.com/stacked-bar-vertical-pharmaspectra-watermark.png',
              },
              yAxis: {
                gridLineWidth: 1,
                labels: {
                  style: {
                    fontSize: '16px',
                  },
                },
              },
              xAxis: {
                labels: {
                  style: {
                    fontSize: '16px',
                  },
                },
              },
            },
            filename: generateExportChartFileName({ exportChartConfig, isTotal: false }),
          }
        : { enabled: false }),
    },
    chart: {
      ...commonChartOptions.chart,
      marginLeft: 40,
      type: 'column',
      className: 'custom-products-mentions-column-chart',
      events: {
        load() {
          setTimeout(this.reflow.bind(this), 0)
        },
      },
    },
    tooltip: {
      ...commonChartOptions.tooltip,
      distance: 19,
      style: {
        width: 300,
      },
      outside: true,
      useHTML: true,
      formatter: function (this: TooltipFormatterContextObject) {
        return toolTipFormatter(this)
      },
    },
    legend: {
      verticalAlign: 'bottom',
      enabled: true,
      y: 0,
      x: 0,
      reversed: true,
      itemMarginBottom: 5,
      align: 'center',
      itemDistance: 8,
      itemWidth: 120,
      symbolHeight: 8,
      symbolWidth: 8,
      symbolRadius: 0,
      borderColor: 'none',
      width: 400,
      itemStyle: {
        fontSize: '10px',
        lineHeight: '16px',
        fontWeight: '400',
      },
    },
    xAxis: {
      ...commonChartOptions.xAxis,
      title: {
        text: dateInterval.breakDownBy === 'week' ? 'Week commencing' : undefined,
        textAlign: 'center',
      },
      labels: {
        skew3d: true,
        rotation: 0,
        style: {
          textOverflow: 'none',
        },
        formatter: function () {
          return `${this.value}`
        },
      },
    },
    yAxis: {
      ...commonChartOptions.yAxis,
      gridLineWidth: 0,
      tickAmount: 11,
      labels: {
        x: -4,
        style: {
          width: 25,
        },
        formatter: function () {
          if (this.isLast || this.isFirst || this.pos === (this.axis.max || 0) / 2) {
            return `${this.value}%`
          }
          return ''
        },
      },
    },
    plotOptions: {
      series: {
        borderWidth: 0,
      },
      column: {
        stacking: 'percent',
        events: {
          legendItemClick: function () {
            return false
          },
        },
      },
    },
    series: data.reverse(),
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              enabled: false,
            },
            xAxis: {
              labels: {
                x: -5,
                formatter: function () {
                  if (this.isLast || this.isFirst) {
                    return `${this.value}`
                  }
                  return ''
                },
              },
            },
          },
        },
      ],
    },
  }
  return [options]
}
